import { useRef, useState, type MouseEvent, type PointerEvent } from "react";

type SwipeId = string | number;

export const useSwipeActions = <T extends SwipeId>() => {
  const [swipedId, setSwipedId] = useState<T | null>(null);
  const gestureRef = useRef<{
    id: T | null;
    startX: number;
    startY: number;
    action: "tap" | "swipe";
  }>({
    id: null,
    startX: 0,
    startY: 0,
    action: "tap",
  });

  const handlePressStart = (event: PointerEvent<HTMLButtonElement>, id: T) => {
    gestureRef.current = {
      id,
      startX: event.clientX,
      startY: event.clientY,
      action: "tap",
    };
  };

  const handlePressMove = (event: PointerEvent<HTMLButtonElement>, id: T) => {
    if (gestureRef.current.id !== id) return;

    const deltaX = event.clientX - gestureRef.current.startX;
    const deltaY = event.clientY - gestureRef.current.startY;

    if (Math.abs(deltaX) > 12 && Math.abs(deltaX) > Math.abs(deltaY)) {
      gestureRef.current.action = "swipe";

      if (deltaX < -36) {
        setSwipedId(id);
      } else if (deltaX > 20 && swipedId === id) {
        setSwipedId(null);
      }
    }
  };

  const handlePressEnd = () => {
    gestureRef.current.id = null;
  };

  const handleActionToggle = (event: MouseEvent<HTMLSpanElement>, id: T) => {
    event.preventDefault();
    event.stopPropagation();
    gestureRef.current.action = "tap";
    gestureRef.current.id = null;
    setSwipedId((currentId) => (currentId === id ? null : id));
  };

  return {
    swipedId,
    setSwipedId,
    gestureRef,
    handlePressStart,
    handlePressMove,
    handlePressEnd,
    handleActionToggle,
  };
};
